import { createRouter, createWebHistory } from "vue-router";
import { useAuth } from "./composables/useAuth.js";
import { useCosmetics } from "./composables/useCosmetics.js";
import { clearUpdateState, handleNavigationError } from "./composables/useAppUpdate.js";
import { LOCAL_GAME_REMOUNT_QUERY } from "./utils/routeViewKey.js";

const LobbyPage = () => import("./pages/LobbyPage.vue");
const GamePage = () => import("./pages/GamePage.vue");
const LocalGamePage = () => import("./pages/LocalGamePage.vue");
const TournamentsLobbyPage = () => import("./pages/TournamentsLobbyPage.vue");
const TournamentPage = () => import("./pages/TournamentPage.vue");
const TournamentHistoryPage = () => import("./pages/TournamentHistoryPage.vue");
const TournamentReplayPage = () => import("./pages/TournamentReplayPage.vue");
const MultiplayerTournamentsPage = () => import("./pages/MultiplayerTournamentsPage.vue");
const GameReplayPage = () => import("./pages/GameReplayPage.vue");
const LiveGameSpectatorPage = () => import("./pages/LiveGameSpectatorPage.vue");
const ProfilePage = () => import("./pages/ProfilePage.vue");
const LearnPage = () => import("./pages/LearnPage.vue");
const InteractiveTutorialPage = () => import("./pages/InteractiveTutorialPage.vue");
const SettingsPage = () => import("./pages/SettingsPage.vue");
const ShopPage = () => import("./pages/ShopPage.vue");
const PuzzlesPage = () => import("./pages/PuzzlesPage.vue");
const PuzzleCreatorPage = () => import("./pages/PuzzleCreatorPage.vue");
const AnalyticsPage = () => import("./pages/AnalyticsPage.vue");
const AdminPage = () => import("./pages/AdminPage.vue");
const PlayersPage = () => import("./pages/PlayersPage.vue");
const ForumPage = () => import("./pages/ForumPage.vue");
const PrivacyPolicyPage = () => import("./pages/PrivacyPolicyPage.vue");
const ImpressumPage = () => import("./pages/ImpressumPage.vue");
const AGBPage = () => import("./pages/AGBPage.vue");
const AboutPage = () => import("./pages/AboutPage.vue");
const ResetPasswordPage = () => import("./pages/ResetPasswordPage.vue");
const VerifyEmailPage = () => import("./pages/VerifyEmailPage.vue");

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: "/", component: LobbyPage },
    { path: "/game/:id", component: GamePage, props: true, meta: { remount: "path" } },
    { path: "/local", component: LocalGamePage, meta: { remount: LOCAL_GAME_REMOUNT_QUERY } },
    // Plural: the hub that names every format. Singular "/tournament" stays the
    // player's own Rasenschach Cup, which is deep-linked from a lot of places.
    { path: "/tournaments", component: TournamentsLobbyPage },
    { path: "/tournament", component: TournamentPage },
    { path: "/tournament/history", component: TournamentHistoryPage },
    { path: "/tournament/history/:tournamentId", component: TournamentPage, props: true, meta: { remount: "path" } },
    { path: "/tournament/shared/:shareId", component: TournamentPage, props: true, meta: { remount: "path" } },
    { path: "/tournament/replay/:matchId", component: TournamentReplayPage, props: true },
    { path: "/multiplayer-tournaments", component: MultiplayerTournamentsPage },
    { path: "/multiplayer-tournaments/:id", component: MultiplayerTournamentsPage, props: true },
    { path: "/replay/:gameId", component: GameReplayPage, props: true, meta: { remount: "path" } },
    { path: "/local/shared/:shareId", component: LiveGameSpectatorPage, props: true, meta: { remount: "path" } },
    { path: "/profile", component: ProfilePage },
    { path: "/profile/:userId", component: ProfilePage, props: true },
    { path: "/learn", component: LearnPage },
    { path: "/learn/tutorial", component: InteractiveTutorialPage },
    { path: "/settings", component: SettingsPage },
    { path: "/shop", component: ShopPage, meta: { requiresShop: true } },
    { path: "/puzzles", component: PuzzlesPage },
    { path: "/puzzles/create", component: PuzzleCreatorPage, meta: { remount: "path" } },
    { path: "/puzzles/:id/edit", component: PuzzleCreatorPage, props: true, meta: { remount: "path" } },
    { path: "/analytics", component: AnalyticsPage, meta: { requiresAdmin: true } },
    { path: "/admin", component: AdminPage, meta: { requiresAdmin: true } },
    { path: "/community/players", component: PlayersPage },
    { path: "/community/forum", component: ForumPage },
    { path: "/community/forum/:postId", component: ForumPage, props: true },
    { path: "/privacy-policy", component: PrivacyPolicyPage },
    { path: "/impressum", component: ImpressumPage },
    { path: "/agb", component: AGBPage },
    { path: "/about", component: AboutPage },
    { path: "/reset-password", component: ResetPasswordPage },
    { path: "/verify-email", component: VerifyEmailPage },
  ],
});

/**
 * Every page here is a lazy import with a content-hashed filename, so a tab
 * left open across a deploy asks for a chunk that no longer exists the moment
 * it navigates. That is a new release, not a failure, and it is handled as one
 * (see useAppUpdate.ts) rather than reaching a page as a raw error string.
 */
router.onError((error, to) => {
  handleNavigationError(error, to.fullPath, router.currentRoute.value.fullPath);
});

// A navigation that worked proves this release is intact.
router.afterEach(() => clearUpdateState());

router.beforeEach(async (to) => {
  // A deployment with the shop switched off has no /shop, even by direct URL.
  if (to.meta?.requiresShop) {
    const { shopAvailable, ensureLoaded } = useCosmetics();
    await ensureLoaded();
    if (!shopAvailable.value) return "/";
  }
  if (!to.meta?.requiresAdmin) return true;
  const { user, fetchCurrentUser } = useAuth();
  if (!user.value) {
    await fetchCurrentUser();
  }
  if (user.value?.isAdmin) return true;
  return "/";
});
